<script setup lang="ts">
import { VIDEO_QUALITY } from '@/contants/game'
import { API_SAVE_CUSTOM_BTN_CONFIG, API_SAVE_GAME_KEY } from '@/api'

import { mobileAndTabletCheck } from '@/utils/cloud-device-sdk/utils'
import { ECloudGameEditBarBtnType, EGameOrientation, EToastType } from '@/types/components'
import type { IGamePlayGroundProps } from '@/types/components'
import { type IPresetKeyboardMap, oldKeyConfig2NewKeyConfig } from '@/utils/cloud-device-sdk'
import type { IUserSaveButtonPayload } from '@/types/api'

import SVG_CTRL_DISCORD from '@/assets/svg/control-bar/discord.svg?raw'
import SVG_CTRL_RETURN from '@/assets/svg/control-bar/return.svg?raw'
import SVG_CTRL_FULLSCREEN from '@/assets/svg/control-bar/fullscreen.svg?raw'
import SVG_CTRL_EXPLAIN from '@/assets/svg/control-bar/explain.svg?raw'
import SVG_CTRL_VOLUME_OPEN from '@/assets/svg/control-bar/volume-open.svg?raw'
import SVG_CTRL_VOLUME_CLOSE from '@/assets/svg/control-bar/volume-close.svg?raw'
import SVG_CTRL_CLARITY from '@/assets/svg/control-bar/clarity.svg?raw'
import SVG_CTRL_DESKTOP from '@/assets/svg/control-bar/desktop.svg?raw'
import SVG_CTRL_EXIT from '@/assets/svg/control-bar/exit.svg?raw'
import SVG_EDIT_PRESET_KEY from '@/assets/svg/control-bar/edit-preset-key.svg?raw'

// edit button position status
import SVG_EDIT_BTN_QUIT from '@/assets/svg/control-bar/edit-btn-quit.svg'
import SVG_EDIT_BTN_RESET from '@/assets/svg/control-bar/edit-btn-reset.svg'
import SVG_EDIT_BTN_SAVE from '@/assets/svg/control-bar/edit-btn-save.svg'
import SVG_TOGGLE_KEYBOARD_TIPS from '@/assets/svg/control-bar/toggle-keyboard-tips.svg'

const props = defineProps<{
  detail: IGamePlayGroundProps
  gameVolume: boolean
  isMobile: boolean
  isFullscreen: boolean
  isNeedSeeAds: boolean
  loadingAds: boolean
  ping: number
  userRemainingTime: number
  currentSelectedQuality: number
  addCountdownTime?: number
  screenWidth: number | undefined
  screenHeight: number | undefined
}>()

const emit = defineEmits(['tapReturnBtn', 'tapFullscreen', 'toggleVolume', 'switchClarity', 'tapExit'])

const customKeyboardDataInject = inject<{
  defaultConfigKeyboardPosition: Record<never, never>
  customSetButtonId: string
  currentStatusConfigKeyboardPosition: string
  isInEditPresetKeyboardStatus: globalThis.Ref<boolean>
  updateIsInEditPresetKeyboardStatus: (salt: boolean) => void
  updateSDKConfigKeyboardPosition: (newInfo: { configKeyboard: IPresetKeyboardMap }) => void
  toggleIsInTipsPresetKeyboardStatus: () => void
}>('custom-keyboard-data')

const isShowCtrlBar = ref<boolean | number>(false)
const ctrlBarRef = ref<HTMLElement>()

const screenSize = computed(() => ({ width: `${props.screenWidth}px`, height: `${props.screenHeight}px` }))

const { t } = useI18n()
const { toast } = useToast()
const { userInfo, isLogged } = useUserInfo()
// const { isMobileOrTablet } = useDevice()

const isMobileOrTablet = computed(() => mobileAndTabletCheck())
// const hoverAddTimeBtn = ref(false)

const qualityList = computed(() => [...VIDEO_QUALITY].map((v, i) => t(`gameDetail.gameQuality${i + 1}`) || v).reverse())
const langSelectedQuality = computed(() => t(`gameDetail.gameQuality${VIDEO_QUALITY.length - props.currentSelectedQuality}`))

const limitSeconds = computed(() => props.userRemainingTime)

const { restTime, addTime } = useCountdown(limitSeconds)
const islandscapeGame = computed(() => props.detail.screenOrientation !== EGameOrientation.PORTRAIT)

const quitAlert = ref(false)

const isShowPublish = computed(() => !Number(import.meta.env.VITE_IS_PROD_ENV) || ['100012023053114570000148806', '100012023032917500000069431', '100012023031317000000057722', '2001240410132398562', '2001240410132293521', '100012023072109450000232467', '2001240625145693532', '100012023042417220000093513'].includes(userInfo.value.uid))

const isShowPresetKeyboard = ref(false)

const ctrlBtnList = computed(() => [
  {
    name: 'return',
    svg: SVG_CTRL_RETURN,
    tooltipText: t('gameDetail.controlBarReturn') || 'Return',
    action: () => emit('tapReturnBtn'),
  },
  {
    name: 'volume',
    tooltipText: t('gameDetail.controlBarVolume') || 'Toggle Volume',
    svg: !props.gameVolume ? SVG_CTRL_VOLUME_OPEN : SVG_CTRL_VOLUME_CLOSE,
    action: () => emit('toggleVolume'),
  },
  {
    name: 'clarity',
    svg: SVG_CTRL_CLARITY,
    action: (params: string) => emit('switchClarity', params),
  },
  // {
  //   name: 'control-explain',
  //   hidden: props.isMobile,
  //   svg: SVG_CTRL_EXPLAIN,
  //   action: () => { },
  // },
  {
    name: 'show-preset-key',
    hidden: props.isMobile,
    // tooltipText: t('gameDetail.controlBarKeyCtrl'),
  },
  {
    name: 'edit-preset-key',
    hidden: props.isMobile,
    svg: SVG_EDIT_PRESET_KEY,
    tooltipText: t('gameDetail.controlBarConfigKey'),
    action: () => customKeyboardDataInject?.updateIsInEditPresetKeyboardStatus(true),
  },
  {
    name: 'desktop',
    hidden: props.isMobile || props.isFullscreen,
    svg: SVG_CTRL_DESKTOP,
    action: () => { },
  },
  {
    name: 'discord',
    svg: SVG_CTRL_DISCORD,
    hidden: true || isMobileOrTablet.value,
    tooltipText: t('gameDetail.controlBarFeedback') || 'Feedback',
    action: () => window.open(import.meta.env.VITE_DISCORD_URL),
  },
  {
    name: 'fullscreen',
    hidden: props.isMobile && props.isFullscreen,
    svg: SVG_CTRL_FULLSCREEN,
    tooltipText: t('gameDetail.controlBarFullscreen') || 'Fullscreen',
    action: () => {
      emit('tapFullscreen')
      ctrlBarRef.value?.focus()
    },
  },
  {
    name: 'exit',
    hidden: !props.isMobile,
    svg: SVG_CTRL_EXIT,
    tooltipText: t('gameDetail.alert9Title') || 'exit',
    action: () => {
      quitAlert.value = true
    },
  },
].filter(i => !i.hidden))

/**
 * 1. 非全屏展示
 *  - 竖屏展示/横屏展示
 * 2. 全屏展示
 *  - 全屏竖屏游戏
 *  - 全屏横屏游戏
 */
const ctrlClass = computed(() => {
  const finalClass = []
  props.isMobile ? finalClass.push('p-4 gap-4') : finalClass.push('p-6 gap-6')

  if (!props.isFullscreen) {
    finalClass.push('relative w-full')
    props.isMobile ? finalClass.push('lt-sm:grid-cols-1 grid-cols-[auto_auto]') : finalClass.push('grid-cols-[auto_auto_1fr]')
  }
  else {
    finalClass.push('fixed bg-transparent bg-gradient-to-t from-#000 to-#000 from-op-90 to-op-60')
    // 移动端特殊处理全屏情况下的横屏游戏
    if (props.isMobile && islandscapeGame.value) {
      // if(islandscapeGame.value){
      finalClass.push('landscape-game-width grid-cols-[auto_auto] left-0 top-0 rotate-z-90 origin-tl property-left')
      isShowCtrlBar.value && finalClass.push('left-18!')
      // }else{
      //   finalClass.push('h-full')
      // }
    }
    // pc端 and 移动端竖屏
    else {
      finalClass.push('w-full left-0 bottom-0 translate-y-100% property-transform')
      props.isMobile ? finalClass.push('grid-cols-1') : finalClass.push('grid-cols-[auto_auto_1fr]')
      isShowCtrlBar.value && finalClass.push('translate-y-0!')
    }
  }

  return finalClass.join(' ')
})

// save btn position / quit / reset
async function tapCloudEditBarBtn(type: ECloudGameEditBarBtnType) {
  const commonPayload = {
    uid: userInfo.value.uid,
    token: userInfo.value.token,
    gameId: props.detail.gameId,
  }
  if (type === ECloudGameEditBarBtnType.PUBLISH) {
    await API_SAVE_GAME_KEY({
      ...commonPayload,
      button: JSON.stringify(customKeyboardDataInject?.currentStatusConfigKeyboardPosition),
    })

    customKeyboardDataInject!.defaultConfigKeyboardPosition = customKeyboardDataInject!.currentStatusConfigKeyboardPosition
    toast({ type: EToastType.SUCCESS, text: 'Success!' })
  }
  else if (type === ECloudGameEditBarBtnType.SAVE) {
    const payload: IUserSaveButtonPayload = {
      configType: 0,
      ...commonPayload,
      configData: JSON.stringify(customKeyboardDataInject?.currentStatusConfigKeyboardPosition),
    }

    if (customKeyboardDataInject?.customSetButtonId)
      payload.buttonId = customKeyboardDataInject.customSetButtonId

    await API_SAVE_CUSTOM_BTN_CONFIG(payload)
    customKeyboardDataInject!.defaultConfigKeyboardPosition = customKeyboardDataInject!.currentStatusConfigKeyboardPosition
    toast({ type: EToastType.SUCCESS, text: 'Success!' })
  }
  else {
    // notify reset default keyboard data
    if (customKeyboardDataInject)
      customKeyboardDataInject.updateSDKConfigKeyboardPosition({ configKeyboard: oldKeyConfig2NewKeyConfig(customKeyboardDataInject.defaultConfigKeyboardPosition, props.detail.screenOrientation) })

    if (type === ECloudGameEditBarBtnType.RESET)
      return
  }
  // quit edit
  isShowPresetKeyboard.value = false
  customKeyboardDataInject?.updateIsInEditPresetKeyboardStatus(false)
}

function toggleShowPresetKeyboard() {
  isShowPresetKeyboard.value = !isShowPresetKeyboard.value
  customKeyboardDataInject?.toggleIsInTipsPresetKeyboardStatus()
}

function setCtrlBarVisible(visible?: boolean) {
  isShowCtrlBar.value = typeof visible !== 'undefined' ? visible : !isShowCtrlBar.value
}

onClickOutside(ctrlBarRef, () => setCtrlBarVisible(false))

watch(() => props.addCountdownTime, () => {
  props.addCountdownTime && addTime(props.addCountdownTime)
})

whenever(() => props.isFullscreen, () => {
  setCtrlBarVisible(true)
  setTimeout(() => setCtrlBarVisible(false), 3000)
})

defineExpose({
  setCtrlBarVisible,
})
</script>

<template>
  <section
    ref="ctrlBarRef" class="grid items-center justify-between bg-gray-2 duration-300 ease-in-out"
    :class="ctrlClass"
  >
    <div
      :class="[!isFullscreen && 'hidden', !isShowCtrlBar ? 'op-100 cursor-pointer' : 'op-0 pointer-events-none']"
      class="absolute left-50% flex-center translate-x--50% transition-all-100 -top-24px" @click="isShowCtrlBar = true"
    >
      <svg
        width="100" height="24" viewBox="0 0 100 24" fill="none" xmlns="http://www.w3.org/2000/svg"
        class="c-#000 c-op-20 transition-all-300 hover:c-op-80"
      >
        <path
          fill="currentColor"
          d="M6.58359 24C11.1289 24 15.284 21.432 17.3167 17.3666L22.6833 6.63344C24.716 2.56803 28.8711 2.5527e-06 33.4164 2.35402e-06L64.5836 9.91659e-07C69.1289 7.92979e-07 73.284 2.56803 75.3167 6.63344L80.6833 17.3666C82.716 21.432 86.8711 24 91.4164 24L100 24L0 24L6.58359 24Z"
        />
        <path
          fill="#fff" fill-opacity="0.9" class="origin-cc transition-all-100 delay-300"
          :class="isShowCtrlBar && 'rotate-180'"
          d="M57.186 13.3953C56.9677 14.0502 56.2598 14.4042 55.6049 14.1859L50.0001 12.3176L44.3954 14.1859C43.7405 14.4042 43.0326 14.0502 42.8143 13.3953C42.596 12.7404 42.9499 12.0325 43.6049 11.8141L49.6049 9.81414C49.8614 9.72861 50.1389 9.72861 50.3954 9.81414L56.3954 11.8141C57.0504 12.0325 57.4043 12.7404 57.186 13.3953Z"
        />
      </svg>
    </div>

    <!-- game icon & game name & ping -->
    <div v-if="!isMobile" class="max-w-40 flex items-center justify-between">
      <img v-if="detail.icon" :src="detail.icon" class="size-10 rd-2" :alt="detail.gameName">
      <div class="mis-4 flex-col">
        <span class="line-clamp-1 max-w-25 text-3.5 c-#fff c-op-90 font-600 lh-3.5">
          {{ detail.gameName }}
        </span>
        <p class="mt-2 flex items-center">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd" clip-rule="evenodd"
              d="M7 2C7.33435 2 7.64658 2.1671 7.83205 2.4453L11.8321 8.4453C12.1384 8.90483 12.0142 9.5257 11.5547 9.83205C11.0952 10.1384 10.4743 10.0142 10.1679 9.5547L7 4.80278L3.83205 9.5547C3.66587 9.80398 3.39667 9.96561 3.09854 9.99513C2.8004 10.0247 2.50474 9.91895 2.29289 9.70711L0.292893 7.70711C-0.0976311 7.31658 -0.0976311 6.68342 0.292893 6.29289C0.683417 5.90237 1.31658 5.90237 1.70711 6.29289L2.84458 7.43036L6.16795 2.4453C6.35342 2.1671 6.66565 2 7 2Z"
              fill="white" fill-opacity="0.5"
            />
          </svg>
          <span class="mis-1 text-3.5 c-#fff c-op-50 font-600 lh-3">
            Ping: {{ ping > 999 ? '999+' : ping }}ms
          </span>
        </p>
      </div>
    </div>

    <!-- remain time -->
    <div
      class="h-10 flex items-center rd-2 bg-gray-3 py-2.5 pie-1 pis-3 lt-md:mis-0" :class="[
        isFullscreen && 'bg-#fff bg-op-10',
        isMobile && 'justify-center',
        isFullscreen && isMobile && !islandscapeGame && 'backdrop-blur-20px [&_.time]:bg-#000 [&_.time]:bg-op-20',
      ]"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd" clip-rule="evenodd"
          d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM10.8281 9.41422L8.99955 7.58564V5C8.99955 4.44772 8.55183 4 7.99955 4C7.44726 4 6.99955 4.44772 6.99955 5L6.99955 8C6.99955 8.28142 7.11579 8.53568 7.30288 8.7174L9.41391 10.8284C9.80443 11.219 10.4376 11.219 10.8281 10.8284C11.2186 10.4379 11.2186 9.80474 10.8281 9.41422Z"
          fill="white" fill-opacity="0.9"
        />
      </svg>
      <!-- <span v-if="!isMobileOrTablet" class="mis-2 text-3.5 c-#fff c-op-90 font-600">{{ t('gameDetail.controlBarTimeCount') || 'Remaining time'
      }}</span> -->
      <div class="mis-1 flex-center flex-shrink-1 ws-nowrap text-4 c-green-5 [&_span]:font-[OSP-DIN]">
        <span
          v-if="restTime >= 3600" class="time min-w-5 inline-flex rd-1 lh-3.5" p="x-1 t-1 b-0.5" bg="gray-2"
          :class="{ 'bg-gray-20': isFullscreen }"
        >
          {{ String(Math.floor(restTime / 60 / 60)).padStart(2, '0') }}
        </span>
        <span v-if="restTime >= 3600" class="inline-block w-2.5 text-center c-green-5 lh-3.5">:</span>
        <span
          class="time min-w-5 inline-flex rd-1 lh-3.5" p="x-1 t-1 b-0.5" bg="gray-2"
          :class="{ 'bg-gray-20': isFullscreen }"
        >
          {{ String(Math.floor(restTime / 60) % 60).padStart(2, '0') }}
        </span>
        <span class="inline-block w-2.5 text-center c-green-5 lh-3.5">:</span>
        <span
          class="time min-w-5 inline-flex rd-1 lh-3.5" p="x-1 t-1 b-0.5" bg="gray-2"
          :class="{ 'bg-gray-20': isFullscreen }"
        >
          {{ String(Math.floor(restTime % 3600 % 60)).padStart(2, '0') }}
        </span>
      </div>

      <!-- see ads btn -->
      <div
        v-if="false"
        class="group mis-3 flex-center cursor-pointer rd-2 bg-gray-5 p-2.5 c-green-5 transition-all-300 hover:bg-green-5 hover:c-gray-1"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd" clip-rule="evenodd" fill="currentColor"
            d="M2.52494 0.037404C1.09383 0.265089 0 1.50481 0 3L4.5 3L2.52494 0.037404ZM4.5 0L6.5 3H9.5L7.5 0H4.5ZM9.5 0L11.5 3H14C14 1.34315 12.6569 0 11 0H9.5ZM14 5H0V11C0 12.6569 1.34315 14 3 14H11C12.6569 14 14 12.6569 14 11V5ZM5.25 8.26619C5.25 7.4889 6.09797 7.00878 6.7645 7.4087L8.82084 8.64251C9.46818 9.03091 9.46818 9.96909 8.82084 10.3575L6.7645 11.5913C6.09797 11.9912 5.25 11.5111 5.25 10.7338V8.26619Z"
          />
        </svg>
        <span class="mis-1 w-0 of-hidden ws-nowrap text-3.5 c-gray-1 font-600 lh-3 group-hover:w-auto">
          Get time
        </span>
      </div>
    </div>

    <div
      class="flex items-center" :class="[
        isFullscreen && '[&_button]:bg-#fff [&_button]:bg-op-20',
        isMobile ? 'gap-3 justify-around' : 'gap-4 justify-end',
      ]"
    >
      <template v-if="!customKeyboardDataInject?.isInEditPresetKeyboardStatus.value">
        <!-- btn -->
        <button
          v-for="(b, i) in ctrlBtnList" :key="i"
          class="group relative size-10 min-w-10 flex-center cursor-default rd-2 bg-gray-3"
          @click="b.tooltipText ? b.action() : () => { }"
        >
          <!-- icon -->
          <div
            v-if="b.name !== 'show-preset-key'"
            class="btn-icon size-full flex-center cursor-pointer c-#fff c-op-90 transition-all-300 group-hover:c-green-5 group-hover:c-op-100"
            v-html="b.svg"
          />
          <div v-else class="btn-icon size-full flex-center flex-col cursor-pointer" @click="toggleShowPresetKeyboard">
            <SVG_TOGGLE_KEYBOARD_TIPS class="h-2 w-4.5" />
            <i class="toggle-dot relative mt-0.5 h-2 w-4.5 b-rd-5.75" :class="[isShowPresetKeyboard ? 'show' : 'hide']" />
          </div>

          <!-- hover icon tooltip -->
          <Tooltip
            v-if="b.name === 'fullscreen' && !isFullscreen && isNeedSeeAds"
            class="left-50% translate-x--50% translate-y--100%" :class="loadingAds && 'pointer-events-auto! op-100!'"
          >
            <GameFullscreen :loading-ads="loadingAds" />
          </Tooltip>
          <Tooltip
            v-else-if="b.tooltipText" :tips-text="b.tooltipText"
            class="left-50% translate-x--50% translate-y--100%"
          />
          <Tooltip v-else class="left-50% translate-x--50% translate-y--100%">
            <div v-if="b.name === 'clarity'" class="flex-col">
              <OptionsList
                :options="qualityList" :selected="langSelectedQuality" bg-color="#333333" param-index
                hover-class="hover-style-1" width="auto" @tap-option="b.action"
              />
              <div class="h-4.5 w-full flex justify-center">
                <svg width="24" height="6" viewBox="0 0 24 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_92_1612)">
                    <path
                      d="M13.2 5.1L17.8667 1.6C19.2514 0.561423 20.9357 -1.33945e-07 22.6667 -5.82818e-08L1.33333 -9.90791e-07C3.06429 -9.15129e-07 4.74856 0.561422 6.13333 1.6L10.8 5.1C11.5111 5.63333 12.4889 5.63333 13.2 5.1Z"
                      fill="#333333"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_92_1612">
                      <rect width="24" height="6" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
            <div v-else-if="b.name === 'control-explain'">
              <GameExplain :package-name="detail.packageName" :explain="detail.description" />
              <div class="h-4 w-full" />
            </div>

            <div v-else-if="b.name === 'desktop'">
              <GameDesktop :game="detail" />
              <div class="h-1rem w-full" />
            </div>
          </Tooltip>
        </button>
      </template>
      <div v-else class="flex">
        <button
          v-if="isShowPublish"
          ld-button="~ black df" class="mr-4 flex-center bg-#262626"
          @click="tapCloudEditBarBtn(ECloudGameEditBarBtnType.PUBLISH)"
        >
          Publish
        </button>
        <button
          ld-button="~ black df" class="mr-4 flex-center bg-#262626"
          @click="tapCloudEditBarBtn(ECloudGameEditBarBtnType.RESET)"
        >
          <SVG_EDIT_BTN_RESET class="mr-1" />
          {{ t(`RTC.interactionLayerReset`) }}
        </button>
        <button
          ld-button="~ black df" class="mr-4 flex-center bg-#262626"
          @click="tapCloudEditBarBtn(ECloudGameEditBarBtnType.QUIT)"
        >
          <SVG_EDIT_BTN_QUIT class="mr-1" />
          {{ t(`RTC.interactionLayerQuit`) }}
        </button>
        <button v-if="isLogged" ld-button="~ green df" class="flex-center" @click="tapCloudEditBarBtn(ECloudGameEditBarBtnType.SAVE)">
          <SVG_EDIT_BTN_SAVE class="mr-1" />
          {{ t(`RTC.interactionLayerSave`) }}
        </button>
      </div>
    </div>
  </section>

  <CommonAlert
    v-if="quitAlert" :title="t('gameDetail.alert9Title')" :content="t('gameDetail.alert9Content')"
    :btn-text="t('gameDetail.alert9Btn')" class="absolute z-12"
    :class="(islandscapeGame && isMobile) && 'rotate-90 -translate-x-50vw w-100vh! transform-gpu'"
    @close="quitAlert = false" @confirm="() => { quitAlert = false; emit('tapExit') }"
  />
</template>

<style lang="scss" scoped>
.landscape-game-width {
  width: v-bind('screenSize.height');
}

.toggle-dot {
  --at-apply: transition-all-300;
  &::before {
    content: '';
    --at-apply: absolute transition-all-150 top-0.25 size-1.5 bg-gray-2 b-rd-50%;
  }
  &.hide {
    --at-apply: bg-#fff bg-op-90;
    &::before {
      --at-apply: left-0.25;
    }
  }

  &.show {
    --at-apply: bg-op-100 bg-green-5;
    &::before {
      --at-apply: left-2.75;
    }
  }
}
</style>
